import { Training, TrainingFormData, ApiResponse, PaginatedResponse } from '../types'
import { getDb } from '../../db/index.js'
import { trainings, attendance, eq, desc, and, gte, lt, sql } from '../../db/schema/index.js'

// Helper function to convert database training to application training
function convertDatabaseTraining(dbTraining: any): Training {
  return {
    ...dbTraining,
    bootcamp: dbTraining.bootcamp === 1,
    free_training: dbTraining.free_training === 1,
    allow_non_bootcamp_join: dbTraining.allow_non_bootcamp_join === 1,
  }
}

export const trainingService = {
  async getTrainings(params?: {
    page?: number
    limit?: number
    status?: 'past' | 'future' | 'today'
    bootcamp?: boolean
  }): Promise<ApiResponse<PaginatedResponse<Training>>> {
    try {
      const db = getDb()

      // Build query conditions
      let conditions = []
      
      if (params?.bootcamp !== undefined) {
        conditions.push(eq(trainings.bootcamp, params.bootcamp ? 1 : 0))
      }

      if (params?.status) {
        const now = new Date()
        const today = new Date(now)
        today.setHours(0, 0, 0, 0)
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)

        switch (params.status) {
          case 'past':
            conditions.push(lt(trainings.datum, today.toISOString()))
            break
          case 'today':
            conditions.push(
              and(
                gte(trainings.datum, today.toISOString()),
                lt(trainings.datum, tomorrow.toISOString())
              )
            )
            break
          case 'future':
            conditions.push(gte(trainings.datum, tomorrow.toISOString()))
            break
        }
      }

      // Get total count
      const countQuery = conditions.length > 0 
        ? db.select({ count: sql<number>`count(*)` }).from(trainings).where(and(...conditions))
        : db.select({ count: sql<number>`count(*)` }).from(trainings)
      
      const countResult = await countQuery
      const total = countResult[0]?.count || 0

      // Apply pagination
      const page = params?.page || 1
      const limit = params?.limit || 10
      const offset = (page - 1) * limit

      // Get paginated results
      let result
      if (conditions.length > 0) {
        result = await db.select()
          .from(trainings)
          .where(and(...conditions))
          .orderBy(desc(trainings.datum))
          .limit(limit)
          .offset(offset)
      } else {
        result = await db.select()
          .from(trainings)
          .orderBy(desc(trainings.datum))
          .limit(limit)
          .offset(offset)
      }

      return {
        success: true,
        data: {
          data: result.map(convertDatabaseTraining),
          total,
          page,
          limit,
        },
        message: 'Trainings fetched successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to fetch trainings',
      }
    }
  },

  async getTraining(id: number): Promise<ApiResponse<Training>> {
    try {
      const db = getDb()
      const result = await db.select().from(trainings).where(eq(trainings.id, id)).limit(1)
      const training = result[0]

      if (!training) {
        return {
          success: false,
          message: 'Training not found',
        }
      }

      return {
        success: true,
        data: convertDatabaseTraining(training),
        message: 'Training fetched successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to fetch training',
      }
    }
  },

  async createTraining(data: TrainingFormData): Promise<ApiResponse<Training>> {
    try {
      const db = getDb()

      const newTraining = {
        titel: data.titel,
        beskrivning: data.beskrivning,
        datum: data.datum,
        location: data.location,
        max_platser: data.max_platser || 20,
        bootcamp: data.bootcamp ? 1 : 0,
        bootcamp_id: data.bootcamp_id,
        free_training: data.free_training ? 1 : 0,
        allow_non_bootcamp_join: data.allow_non_bootcamp_join ? 1 : 0,
        created_at: new Date().toISOString(),
        anmalda: 0,
        bootcamp_name: data.bootcamp_name,
      }

      const result = await db.insert(trainings).values(newTraining).returning()
      const createdTraining = result[0]

      return {
        success: true,
        data: convertDatabaseTraining(createdTraining),
        message: 'Training created successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to create training',
      }
    }
  },

  async updateTraining(id: number, data: Partial<TrainingFormData>): Promise<ApiResponse<Training>> {
    try {
      const db = getDb()

      // Check if training exists
      const existingResult = await db.select().from(trainings).where(eq(trainings.id, id)).limit(1)
      if (!existingResult[0]) {
        return {
          success: false,
          message: 'Training not found',
        }
      }

      const updateData: any = {}
      if (data.titel !== undefined) updateData.titel = data.titel
      if (data.beskrivning !== undefined) updateData.beskrivning = data.beskrivning
      if (data.datum !== undefined) updateData.datum = data.datum
      if (data.location !== undefined) updateData.location = data.location
      if (data.max_platser !== undefined) updateData.max_platser = data.max_platser
      if (data.bootcamp !== undefined) updateData.bootcamp = data.bootcamp ? 1 : 0
      if (data.bootcamp_id !== undefined) updateData.bootcamp_id = data.bootcamp_id
      if (data.free_training !== undefined) updateData.free_training = data.free_training ? 1 : 0
      if (data.allow_non_bootcamp_join !== undefined) updateData.allow_non_bootcamp_join = data.allow_non_bootcamp_join ? 1 : 0
      if (data.bootcamp_name !== undefined) updateData.bootcamp_name = data.bootcamp_name

      const result = await db.update(trainings)
        .set(updateData)
        .where(eq(trainings.id, id))
        .returning()
      
      const updatedTraining = result[0]

      return {
        success: true,
        data: convertDatabaseTraining(updatedTraining),
        message: 'Training updated successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to update training',
      }
    }
  },

  async deleteTraining(id: number): Promise<ApiResponse> {
    try {
      const db = getDb()

      // Check if training exists
      const existingResult = await db.select().from(trainings).where(eq(trainings.id, id)).limit(1)
      if (!existingResult[0]) {
        return {
          success: false,
          message: 'Training not found',
        }
      }

      await db.delete(trainings).where(eq(trainings.id, id))

      return {
        success: true,
        message: 'Training deleted successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to delete training',
      }
    }
  },

  async duplicateTraining(id: number): Promise<ApiResponse<Training>> {
    try {
      const db = getDb()

      const originalResult = await db.select().from(trainings).where(eq(trainings.id, id)).limit(1)
      const originalTraining = originalResult[0]
      
      if (!originalTraining) {
        return {
          success: false,
          message: 'Training not found',
        }
      }

      // Create a duplicate with a new date (next week)
      const duplicateDate = new Date(originalTraining.datum)
      duplicateDate.setDate(duplicateDate.getDate() + 7)

      const duplicatedTraining = {
        titel: `${originalTraining.titel} (Copy)`,
        beskrivning: originalTraining.beskrivning,
        datum: duplicateDate.toISOString(),
        location: originalTraining.location,
        max_platser: originalTraining.max_platser,
        bootcamp: originalTraining.bootcamp,
        bootcamp_id: originalTraining.bootcamp_id,
        free_training: originalTraining.free_training,
        allow_non_bootcamp_join: originalTraining.allow_non_bootcamp_join,
        created_at: new Date().toISOString(),
        anmalda: 0,
        bootcamp_name: originalTraining.bootcamp_name,
      }

      const result = await db.insert(trainings).values(duplicatedTraining).returning()
      const createdTraining = result[0]

      return {
        success: true,
        data: convertDatabaseTraining(createdTraining),
        message: 'Training duplicated successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to duplicate training',
      }
    }
  },

  async getTrainingAttendance(id: number): Promise<ApiResponse<any[]>> {
    try {
      const db = getDb()

      const result = await db.select().from(attendance).where(eq(attendance.training_id, id))

      return {
        success: true,
        data: result,
        message: 'Attendance fetched successfully'
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to fetch attendance',
      }
    }
  },

  async markAttendance(trainingId: number, userId: number, present: boolean): Promise<ApiResponse> {
    try {
      const db = getDb()

      // Check if attendance record already exists
      const existingResult = await db.select()
        .from(attendance)
        .where(and(eq(attendance.training_id, trainingId), eq(attendance.user_id, userId)))
        .limit(1)
      
      const existingRecord = existingResult[0]

      if (existingRecord) {
        // Update existing record
        await db.update(attendance)
          .set({
            present: present ? 1 : 0,
            timestamp: new Date().toISOString(),
          })
          .where(eq(attendance.id, existingRecord.id))
      } else {
        // Add new attendance record
        await db.insert(attendance).values({
          training_id: trainingId,
          user_id: userId,
          present: present ? 1 : 0,
          timestamp: new Date().toISOString(),
        })
      }

      return {
        success: true,
        message: `Attendance ${present ? 'marked as present' : 'marked as absent'}`
      }
    } catch (error: any) {
      console.error('Database error:', error)
      return {
        success: false,
        message: 'Failed to update attendance',
      }
    }
  },
}
